import { useState } from 'react'
import {ChevronDown, ChevronUp} from 'lucide-react'

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  const faqs = [
    {
      question: "What is a shift roster calculator?",
      answer: "A shift roster calculator is an online tool that helps you plan and visualize rotating work schedules. Simply input your start date and rotation pattern (like 4 on, 4 off), and the calculator generates your complete roster showing which days you'll be working and which days you'll have off."
    },
    {
      question: "How does the 8-6-7-7 roster calculator work?",
      answer: "The 8-6-7-7 roster is a specialized rotation pattern common in mining and FIFO operations. It means 8 days on, 6 days off, then 7 days on, 7 days off - a 28-day cycle. Our calculator automatically handles this complex pattern and displays your complete schedule for months ahead."
    },
    {
      question: "Can I use this for a 9-5 roster calculation?",
      answer: "Yes! The 9-5 roster (9 days on, 5 days off) is fully supported. This pattern is popular in offshore, oil & gas, and remote work industries. Simply select the 9-5 template from the sidebar or enter 9 days on and 5 days off in the calculator."
    },
    {
      question: "Is this roster calculator free to use?",
      answer: "Yes, our roster calculator is completely free to use online. There are no hidden fees, registration requirements, or limitations. Calculate as many rosters as you need for your shift planning."
    },
    {
      question: "Can I calculate rosters for my entire team?",
      answer: "Currently, the calculator shows one person's roster at a time. For team scheduling, you can calculate multiple rosters by changing the start date for each team member. We recommend staggering start dates to ensure adequate coverage."
    },
    {
      question: "Can I share a calculated roster with my team?",
      answer: "Yes. After generating a roster, click Share next to the PDF and Excel buttons to create a shareable roster link. The link carries the rotation pattern, start date and duration, so anyone who opens it sees the same schedule instantly without signing in. It contains no personal details, and the pattern can be changed again on their end."
    },
    {
      question: "Can I export my roster to my calendar app?",
      answer: "Yes. The Calendar button downloads an .ics (iCalendar) file you can open in Outlook, Google Calendar, Apple Calendar or any calendar app that supports ICS import. Every work day in the roster is added as an all-day shift event, so your schedule appears next to your other appointments."
    },
    {
      question: "Can I get email reminders before my shifts?",
      answer: "Yes. Sign in, calculate your roster and click Reminders. Choose whether to be reminded about the first day of each work block or every work day, how many days ahead and at what time. Reminder emails are sent automatically to your account email and cover the next 60 days."
    },
    {
      question: "What rotation patterns are supported?",
      answer: "Our roster calculator online supports any rotation pattern you can imagine: 4-4, 7-7, 2-2, 8-6-7-7, 9-5, 14-14, and any custom combination. Just enter your days on and days off, and we'll calculate the complete schedule."
    }
  ]

  return (
    <section id="faq" className="py-16 bg-slate-50">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-slate-600">
            Common questions about using our shift roster calculator
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-4 flex justify-between items-center text-left hover:bg-slate-50 transition-colors"
              >
                <h3 className="text-lg font-semibold text-slate-900 pr-4">
                  {faq.question}
                </h3>
                {openIndex === index ? (
                  <ChevronUp className="w-5 h-5 text-teal-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                )}
              </button>
              {openIndex === index && (
                <div className="px-6 pb-4 text-slate-600 leading-relaxed">
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
